import { PencilIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import { useToast } from '../../../context/ToastContext';
import { useUser } from '../../../hooks/useUser';

const AccountingRegulation: React.FC = () => {
  const { user, loading, updating, updateUser } = useUser();
  const { showToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedSkr, setSelectedSkr] = useState<'SKR03' | 'SKR04'>('SKR03');
  const [isSmallBusiness, setIsSmallBusiness] = useState(false);

  // Initialize selected SKR and isSmallBusiness when user data is loaded
  useEffect(() => {
    if (user?.skr) {
      setSelectedSkr(user.skr);
    }
    if (user?.isSmallBusiness !== undefined) {
      setIsSmallBusiness(user.isSmallBusiness);
    }
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateUser({
        skr: selectedSkr,
        isSmallBusiness: isSmallBusiness,
      });
      showToast(
        'Accounting regulation updated',
        'Your accounting regulation has been updated successfully.',
        'success'
      );
      setIsEditing(false);
    } catch (err) {
      showToast(
        'Failed to update accounting regulation',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleCancel = () => {
    // Reset to current user SKR and isSmallBusiness
    if (user?.skr) {
      setSelectedSkr(user.skr);
    }
    setIsSmallBusiness(user?.isSmallBusiness ?? false);
    setIsEditing(false);
  };

  // Check if SKR or isSmallBusiness has been changed from the current value
  const hasChanged =
    selectedSkr !== user?.skr || isSmallBusiness !== (user?.isSmallBusiness ?? false);

  if (loading) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='animate-pulse'>
          <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4'></div>
          <div className='h-20 bg-gray-200 dark:bg-gray-700 rounded'></div>
        </div>
      </div>
    );
  }

  // If editing, show edit form
  if (isEditing) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='flex justify-between items-start mb-4 min-h-[40px]'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
            Accounting Regulation
          </h2>
        </div>

        <form onSubmit={handleSave} className='space-y-3'>
          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-3'>
              Select accounting scheme
            </span>
            <div className='flex gap-6'>
              <label className='flex items-center space-x-2 cursor-pointer'>
                <input
                  type='radio'
                  name='skr'
                  value='SKR03'
                  checked={selectedSkr === 'SKR03'}
                  onChange={e =>
                    setSelectedSkr(e.target.value as 'SKR03' | 'SKR04')
                  }
                  className='w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-gray-900 dark:text-white'>SKR03</span>
              </label>
              <label className='flex items-center space-x-2 cursor-pointer'>
                <input
                  type='radio'
                  name='skr'
                  value='SKR04'
                  checked={selectedSkr === 'SKR04'}
                  onChange={e =>
                    setSelectedSkr(e.target.value as 'SKR03' | 'SKR04')
                  }
                  className='w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-gray-900 dark:text-white'>SKR04</span>
              </label>
            </div>
          </div>

          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <label className='flex items-center space-x-2 cursor-pointer'>
              <input
                type='checkbox'
                checked={isSmallBusiness}
                onChange={e => setIsSmallBusiness(e.target.checked)}
                className='w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2 rounded'
              />
              <span className='text-sm font-medium text-gray-900 dark:text-white'>
                Small business
              </span>
            </label>
          </div>

          <div className='flex justify-end gap-2 pt-1'>
            <button
              type='button'
              onClick={handleCancel}
              disabled={updating}
              className='px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={updating || !hasChanged}
              className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Display mode: Show current SKR with edit button
  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
      <div className='flex justify-between items-start mb-4 min-h-[40px]'>
        <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
          Accounting Regulation
        </h2>
        <button
          onClick={() => setIsEditing(true)}
          className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-md transition-colors'
          aria-label='Edit accounting regulation'
        >
          <PencilIcon className='w-5 h-5' />
        </button>
      </div>

      <div className='space-y-3'>
        <div className='pb-3'>
          <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
            Current Scheme
          </span>
          <p className='text-gray-900 dark:text-white font-medium text-lg'>
            {user?.skr || 'Not set'}
          </p>
        </div>
        <div className='pb-3'>
          <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
            Small Business
          </span>
          <p className='text-gray-900 dark:text-white font-medium text-lg'>
            {user?.isSmallBusiness ? 'Yes' : 'No'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountingRegulation;
